import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Factory,
  ArrowLeft,
  Package,
  Zap,
  Clock,
  List,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';

const PlantaDashboard = () => {
  const { idPlanta } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [planta, setPlanta] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadPlanta = useCallback(async () => {
    setIsLoading(true);
    try {
      const { plantasService } = await import('@/pages/parametrizacion/plantas/utils/plantasService');
      const response = await plantasService.getById(idPlanta);
      setPlanta(response?.data || null);
    } catch (error) {
      console.error('Error cargando planta:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la información de la planta.',
        variant: 'destructive'
      });
      navigate('/plantas');
    } finally {
      setIsLoading(false);
    }
  }, [idPlanta, toast, navigate]);

  useEffect(() => {
    loadPlanta();
  }, [loadPlanta]);

  const menuItems = [
    {
      id: 'deposito-ropa',
      title: 'Recepción de Ropa',
      description: 'Gestión de recepción y entrega de prendas',
      icon: Package,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      path: `/plantas/${idPlanta}/deposito-ropa`,
      available: true
    },
    {
      id: 'inventario',
      title: 'Gestión de prendas',
      description: 'Gestión de inventario de prendas',
      icon: List,
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      path: `/plantas/${idPlanta}/inventario`,
      available: true
    },
/*     {
      id: 'servicios-publicos',
      title: 'Registro Servicios Públicos',
      description: 'Control de consumo de agua, luz y gas',
      icon: Zap,
      color: 'bg-amber-500',
      hoverColor: 'hover:bg-amber-600',
      path: `/plantas/${idPlanta}/servicios-publicos`,
      available: true
    },
    {
      id: 'entrada-salida',
      title: 'Registro Entrada/Salida Personal',
      description: 'Control de asistencia y horarios del personal',
      icon: Clock,
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      path: `/plantas/${idPlanta}/entrada-salida`,
      available: false
    }, */

  ];

  const handleNavigate = (item) => {
    if (item.available) {
      navigate(item.path);
    } else {
      toast({
        title: 'Próximamente',
        description: 'Esta funcionalidad estará disponible pronto.',
        variant: 'default'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-muted rounded-lg"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!planta) {
    return (
      <div className="container mx-auto p-4 md:p-6 lg:p-8">
        <Card>
          <CardContent className="p-12 text-center">
            <Factory className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">Planta no encontrada</h3>
            <p className="text-muted-foreground mb-4">
              No se pudo encontrar la información de esta planta.
            </p>
            <Button onClick={() => navigate('/plantas')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Plantas
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto p-4 md:p-6 lg:p-8"
    >
      {/* Header con información de la planta */}
      <Card className="mb-6 border-2 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="bg-primary/10 p-4 rounded-lg">
                <Factory className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-foreground">{planta.nombre}</h1>
                  <span className="px-3 py-1 rounded-full text-sm font-medium  bg-[#7ac143] text-white">
                    {planta.estado}
                  </span>
                </div>
                <p className="text-muted-foreground">Dashboard de gestión y operaciones</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/plantas')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
          </div>



          {/* Menú de funcionalidades */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card
                    className={`cursor-pointer transition-all duration-200 hover:shadow-xl border-2 hover:border-primary ${!item.available ? 'opacity-60' : ''
                      }`}
                    onClick={() => handleNavigate(item)}
                  >
                    <CardContent className="p-6 h-72">
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className={`${item.color} ${item.hoverColor} p-6 rounded-2xl transition-colors duration-200`}>
                          <Icon className="h-12 w-12 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-foreground mb-2">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                        {!item.available && (
                          <span className="text-xs px-3 py-1 bg-muted text-muted-foreground rounded-full">
                            Próximamente
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      {/* Información adicional */}
      <Card className="mt-6 bg-muted/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 p-2 rounded-lg">
              <Factory className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-1">Gestión Centralizada</h3>
              <p className="text-sm text-muted-foreground">
                Desde este dashboard puedes acceder a todas las funcionalidades operativas de la planta {planta.nombre}.
                Cada módulo está diseñado para optimizar la gestión diaria y el control de operaciones.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PlantaDashboard;
